import { useQueryClient } from "@tanstack/react-query";
import classnames from "classnames";
import { ArrowRight } from "lucide-react";
import { motion, type Variants } from "motion/react";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@krea/shared/design-system/Button";
import { Spinner } from "@krea/shared/design-system/Spinner";
import { useDebounce } from "@krea/shared/utils/use-debounce";

import { AssignLenderUserSelect } from "@/components/assign-lender-user-select";
import { useSearchLenderApplications } from "@/features/lender-applications/hooks/data/use-get-lender-application-search-list.query";
import { queryKeyMap } from "@/features/query-client";

import { StatusLabel } from "../components/status-label";
import settings from "../settings";
import { capitalizeWithSpaces, formatTime } from "../utils";
import { useMobile } from "../utils/customHooks/useMobile";

const variants: Variants = {
  initial: {
    opacity: 0,
    x: -100,
  },
  visible: (custom: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: custom * 0.05,
      type: "tween",
      duration: 0.2,
      ease: "easeOut",
    },
  }),
};

const ListItem = ({
  className,
  title,
  value,
}: {
  className: string;
  title: string;
  value: string;
}) => {
  return (
    <dl
      className={classnames(
        "flex w-full items-baseline justify-between gap-4",
        className,
      )}
    >
      <dt className="m-0 shrink-0 font-medium text-gray-900">
        {title}:
      </dt>
      <dd className="m-0 min-w-0 flex-1 text-right text-gray-500">
        {value}
      </dd>
    </dl>
  );
};

const SearchLenderApplications = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const isMobile = useMobile();
  const { lenderCompanyId } = useParams();
  const { data: searchedLenderApplicationsData, isPending } =
    useSearchLenderApplications(Number(lenderCompanyId), debouncedSearch);

  const queryClient = useQueryClient();
  const optimisticUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeyMap.searchLenderApplications(debouncedSearch),
    });
  }, [debouncedSearch, queryClient]);

  const goToDetails = (searchedLenderApplicationId: number) => {
    navigate(
      `/lender-company/${lenderCompanyId}/lender-application/${searchedLenderApplicationId}`,
    );
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={settings.routeAnimation.pageVariants}
      transition={settings.routeAnimation.pageTransition}
    >
      <div className="mb-4">
        <label className="mb-2 block text-xl font-bold">
          <span className="inline-flex items-center gap-2">
            Search
            {isPending && (
              <span className="inline-flex shrink-0">
                <Spinner aria-hidden className="size-4" />
              </span>
            )}
          </span>
        </label>
        <input
          type="text"
          name="search"
          className="block w-full rounded border border-gray-300 px-3 py-2 text-base focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          value={search}
          placeholder="Org number, Org name, ID"
          onChange={(event) => setSearch(event.target.value)}
          autoComplete="off"
          aria-label="Search lender applications"
        />
      </div>
      {searchedLenderApplicationsData?.items &&
        searchedLenderApplicationsData.items.length === 0 && (
          <p className="text-center">No results</p>
        )}
      {!isMobile ? (
        <table className="w-full min-w-0 table-fixed text-sm [&_td]:px-2 [&_td]:py-2 [&_th]:px-2 [&_th]:py-2">
          <tbody>
            {searchedLenderApplicationsData?.items.map(
              (searchedLenderApplication) => (
                <motion.tr
                  key={searchedLenderApplication.id}
                  initial="initial"
                  animate="visible"
                  variants={variants}
                  className="border-y border-gray-200 py-2 hover:bg-primary-100"
                >
                  <td>
                    <Button
                      variant="link"
                      size="icon"
                      className="p-0"
                      onClick={() => goToDetails(searchedLenderApplication.id)}
                    >
                      {searchedLenderApplication.id}
                    </Button>
                  </td>
                  <td className="whitespace-nowrap">
                    {formatTime(
                      searchedLenderApplication.markedEligibleForLenderAt,
                    )}
                  </td>
                  <td>
                    {`${searchedLenderApplication.loanApplication.applyingCompany.customerCompany.name} (${searchedLenderApplication.loanApplication.applyingCompany.customerCompany.organisationNumber})`}
                  </td>
                  <td className="whitespace-nowrap">
                    {capitalizeWithSpaces(searchedLenderApplication.product)}
                  </td>
                  <td className="whitespace-nowrap">
                    <StatusLabel
                      type={searchedLenderApplication.type}
                      hasActiveLenderResponses={
                        searchedLenderApplication.hasActiveLenderResponses
                      }
                      hideTooltip={true}
                    />
                  </td>
                  <td>
                    <AssignLenderUserSelect
                      lenderApplicationId={searchedLenderApplication.id}
                      assignedLenderUser={
                        searchedLenderApplication.assignedLenderUser
                      }
                      className="w-28"
                      onSuccess={optimisticUpdate}
                    />
                  </td>

                  <td>
                    <div className="flex justify-center">
                      <Button
                        variant="default"
                        size="icon"
                        onClick={() =>
                          goToDetails(searchedLenderApplication.id)
                        }
                        aria-label={`Open application ${searchedLenderApplication.id}`}
                      >
                        <ArrowRight className="size-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ),
            )}
          </tbody>
        </table>
      ) : (
        <div>
          {searchedLenderApplicationsData?.items.map(
            (searchedLenderApplication) => (
              <motion.section
                key={searchedLenderApplication.id}
                initial="initial"
                animate="visible"
                variants={variants}
                className="mt-4 p-4 shadow-sm"
              >
                <div className="mb-3 flex w-full items-start justify-between gap-4 text-sm">
                  <div className="shrink-0 font-medium text-gray-900">
                    {`#${searchedLenderApplication.id}`}
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    {`${searchedLenderApplication.loanApplication.applyingCompany.customerCompany.name} (${searchedLenderApplication.loanApplication.applyingCompany.customerCompany.organisationNumber})`}
                  </div>
                </div>
                <div className="w-full space-y-2 pb-4">
                  <ListItem
                    className="m-0"
                    title="Product"
                    value={` ${capitalizeWithSpaces(searchedLenderApplication.product)}`}
                  />
                  <ListItem
                    className="m-0"
                    title="Received"
                    value={formatTime(
                      searchedLenderApplication.markedEligibleForLenderAt,
                    )}
                  />
                </div>
                <div className="mb-4">
                  <div className="mb-2 block text-sm font-medium">
                    Assigned to
                  </div>
                  <AssignLenderUserSelect
                    lenderApplicationId={searchedLenderApplication.id}
                    assignedLenderUser={
                      searchedLenderApplication.assignedLenderUser
                    }
                    className="w-full"
                    onSuccess={optimisticUpdate}
                  />
                </div>
                <div>
                  <div className="-mx-4 flex justify-between px-4 pt-4 align-middle">
                    <StatusLabel
                      type={searchedLenderApplication.type}
                      hasActiveLenderResponses={
                        searchedLenderApplication.hasActiveLenderResponses
                      }
                      hideTooltip={true}
                    />

                    <Button
                      className="mt-2"
                      variant="default"
                      size="icon"
                      onClick={() => goToDetails(searchedLenderApplication.id)}
                      aria-label={`Open application ${searchedLenderApplication.id}`}
                    >
                      <ArrowRight className="size-4" />
                    </Button>
                  </div>
                </div>
              </motion.section>
            ),
          )}
        </div>
      )}
    </motion.div>
  );
};

export default SearchLenderApplications;
